// ==========================
// Harmony / reharmonization helpers
// ==========================

import { Chord, Key, Measure, PitchedNote, Rest, TimeSignature, Tuplet, Voice } from "./music-objects";

export type DetectedChordLike = {
    displayName: string;
    rootPc: number;          // 0..11
    pitchClasses: number[];  // 0..11
};

export type PitchedAtom = {
    startDiv: number;        // start offset in Measure divisions
    durDiv: number;          // duration in Measure divisions
    notes: PitchedNote[];    // pitched notes sounding together (1 for single note, >1 for chord)
    isChordEvent: boolean;
};

type MelodyItem = {
    startDiv: number;
    durDiv: number;
    notes: PitchedNote[];    // tie-group notes to shift together
    origMidi: number;        // taken from first note
};

export function mod12(n: number): number {
    const x = n % 12;
    return x < 0 ? x + 12 : x;
}

function clamp(n: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, n));
}

function clamp01(x: number): number {
    if (!Number.isFinite(x)) return 0;
    return Math.max(0, Math.min(1, x));
}

function pcOfMidi(m: number): number {
    return mod12(m);
}

/**
 * Major key tonic pitch-class from MusicXML-style fifths.
 * fifths=0 => C (0), fifths=+1 => G (7), fifths=-1 => F (5), etc.
 */
function majorTonicPcFromFifths(fifths: number): number {
    return mod12(7 * fifths);
}

/**
 * Major scale pitch-classes for Key.fifths.
 * If you later add minor-mode to Key, you can swap intervals here.
 */
export function majorScalePcsForKey(key: Key): number[] {
    const tonic = majorTonicPcFromFifths(key.fifths);
    const major = [0, 2, 4, 5, 7, 9, 11];
    return major.map((iv) => mod12(tonic + iv));
}

/**
 * Same beatStrength curve you used earlier (quarter-note beat units).
 */
function beatStrength(beat: number, beatsPerMeasure: number): number {
    const nearest = Math.round(beat);
    const dist = Math.abs(beat - nearest);

    let s =
        dist < 1e-6 ? 1.00 :
            dist < 0.125 ? 0.82 :
                dist < 0.25 ? 0.68 :
                    dist < 0.375 ? 0.55 :
                        0.42;

    if (Math.abs(beat - 0) < 1e-6) s *= 1.25;

    const half = beatsPerMeasure / 2;
    if (Number.isInteger(half) && Math.abs(beat - half) < 1e-6) s *= 1.08;

    return s;
}

function getTimeSigBeats(timeSignature: TimeSignature): number {
    // Adjust here if your numerator property is named differently.
    const anyTs = timeSignature as any;
    return Number.isFinite(anyTs.beats) ? anyTs.beats : 4;
}

function getTimeSigBeatType(timeSignature: TimeSignature): number {
    const anyTs = timeSignature as any;
    return Number.isFinite(anyTs.beatType) ? anyTs.beatType : 4;
}

/**
 * Quarter-note units per measure, so beat positions line up with your MidiNote.startBeat convention
 * (your toMidiNote uses duration/divisions).
 */
function quarterBeatsPerMeasure(measure: Measure): number {
    const beats = getTimeSigBeats(measure.timeSignature);
    const beatType = getTimeSigBeatType(measure.timeSignature);
    return beats * (4 / beatType);
}

function isChordTonePc(pc: number, chordPcSet: Set<number>): boolean {
    return chordPcSet.has(mod12(pc));
}

function isScaleTonePc(pc: number, scalePcSet: Set<number>): boolean {
    return scalePcSet.has(mod12(pc));
}

export function applyShiftToMidi(note: PitchedNote, targetMidi: number, key: Key): void {
    const cur = note.pitch.getMidiPitch();
    const delta = targetMidi - cur;
    if (delta === 0) return;
    // Keep within MIDI range
    const newMidi = clamp(cur + delta, 0, 127);
    note.pitch.shift(newMidi - cur, key);
}

export function collectPitchedAtomsFromVoice(voice: Voice, _: Measure): PitchedAtom[] {
    const atoms: PitchedAtom[] = [];
    let t = 0; // in divisions

    const pushChordLike = (startDiv: number, durDiv: number, notes: PitchedNote[], isChordEvent: boolean) => {
        if (notes.length === 0) return;
        atoms.push({ startDiv, durDiv, notes, isChordEvent });
    };

    for (const ev of voice.notationEvents) {
        if (ev instanceof Rest) {
            t += ev.duration;
            continue;
        }

        if (ev instanceof PitchedNote) {
            pushChordLike(t, ev.duration, [ev], false);
            t += ev.duration;
            continue;
        }

        if (ev instanceof Chord) {
            const pitched = ev.notes.filter((n): n is PitchedNote => n instanceof PitchedNote);
            pushChordLike(t, ev.duration, pitched, pitched.length > 1);
            t += ev.duration;
            continue;
        }

        if (ev instanceof Tuplet) {
            // Distribute tuplet.totalDuration across inner events proportionally by inner duration.
            const total = ev.duration;
            const inner = ev.notationEvents;

            const sumInner = inner.reduce((a, b) => a + b.duration, 0) || 1;
            let tt = t;

            for (const innerEv of inner) {
                const span = total * (innerEv.duration / sumInner);

                if (innerEv instanceof Rest) {
                    tt += span;
                    continue;
                }

                if (innerEv instanceof PitchedNote) {
                    pushChordLike(tt, span, [innerEv], false);
                    tt += span;
                    continue;
                }

                if (innerEv instanceof Chord) {
                    const pitched = innerEv.notes.filter((n): n is PitchedNote => n instanceof PitchedNote);
                    pushChordLike(tt, span, pitched, pitched.length > 1);
                    tt += span;
                    continue;
                }

                // BasicNotationEvent should only be Rest/Note/Chord, so ignore otherwise.
                tt += span;
            }

            t += total;
            continue;
        }

        // Unknown NotationEvent -> just advance time to be safe
        t += ev.duration;
    }

    return atoms;
}

export function chooseMelodyVoiceIndex(measure: Measure): number {
    if (measure.voices.length <= 1) return 0;

    let bestIdx = 0;
    let bestScore = -Infinity;

    for (let i = 0; i < measure.voices.length; i++) {
        const atoms = collectPitchedAtomsFromVoice(measure.voices[i], measure);
        const pitchedNotes = atoms.flatMap((a) => a.notes);
        if (pitchedNotes.length === 0) continue;

        const avgMidi =
            pitchedNotes.reduce((s, n) => s + n.pitch.getMidiPitch(), 0) / pitchedNotes.length;

        const lyricCount = pitchedNotes.reduce((c, n) => c + (n.lyric ? 1 : 0), 0);
        const lyricRatio = lyricCount / pitchedNotes.length;

        const longCount = atoms.reduce((c, a) => c + (a.durDiv >= measure.divisions ? 1 : 0), 0);
        const longRatio = longCount / Math.max(1, atoms.length);

        // Lyrics dominate, then pitch height, then sustained-ness
        const score = lyricRatio * 100 + avgMidi * 0.25 + longRatio * 5;

        if (score > bestScore) {
            bestScore = score;
            bestIdx = i;
        }
    }

    return bestIdx;
}

export function pickTopNote(notes: PitchedNote[]): PitchedNote {
    let best = notes[0];
    let bestMidi = best.pitch.getMidiPitch();
    for (let i = 1; i < notes.length; i++) {
        const m = notes[i].pitch.getMidiPitch();
        if (m > bestMidi) {
            bestMidi = m;
            best = notes[i];
        }
    }
    return best;
}

/**
 * Group consecutive melody notes that are tied so we shift them together.
 * We don’t have explicit tie linkage, so we use a tolerant heuristic:
 * - If current note has a tie and next note also has a tie, treat as same group.
 */
export function groupTiedMelodyItems(items: Array<{ atom: PitchedAtom; melodyNote: PitchedNote }>): MelodyItem[] {
    const out: MelodyItem[] = [];
    let i = 0;

    while (i < items.length) {
        const first = items[i];
        const groupNotes: PitchedNote[] = [first.melodyNote];

        let startDiv = first.atom.startDiv;
        let durDiv = first.atom.durDiv;

        let j = i;
        while (j + 1 < items.length) {
            const a = items[j];
            const b = items[j + 1];

            const aTie = (a.melodyNote.tie as any)?.type?.toString?.().toLowerCase?.() ?? "";
            const bTie = (b.melodyNote.tie as any)?.type?.toString?.().toLowerCase?.() ?? "";
            const aHasTie = a.melodyNote.tie != null || aTie.length > 0;
            const bHasTie = b.melodyNote.tie != null || bTie.length > 0;

            // Heuristic: group if both sides look tied
            if (!(aHasTie && bHasTie)) break;

            groupNotes.push(b.melodyNote);
            durDiv += b.atom.durDiv;
            j++;
        }

        out.push({
            startDiv,
            durDiv,
            notes: groupNotes,
            origMidi: first.melodyNote.pitch.getMidiPitch(),
        });

        i = j + 1;
    }

    return out;
}

function enumerateCandidatesMidi(
    origMidi: number,
    scalePcSet: Set<number>,
    maxRadius: number
): number[] {
    const o = Math.round(origMidi);
    const candidates: number[] = [];

    for (let r = 0; r <= maxRadius; r++) {
        const a = o - r;
        const b = o + r;

        if (a >= 0 && a <= 127 && isScaleTonePc(pcOfMidi(a), scalePcSet)) candidates.push(a);
        if (b !== a && b >= 0 && b <= 127 && isScaleTonePc(pcOfMidi(b), scalePcSet)) candidates.push(b);

        // Keep candidate set small
        if (candidates.length >= 12) break;
    }

    // Ensure at least something
    if (candidates.length === 0) {
        candidates.push(clamp(o, 0, 127));
    }

    // unique
    return [...new Set(candidates)];
}

function nearestMidiInPcSet(
    origMidi: number,
    pcSet: Set<number>,
    lo: number,
    hi: number,
    maxRadius: number
): number | null {
    const o = Math.round(origMidi);
    lo = clamp(lo, 0, 127);
    hi = clamp(hi, 0, 127);
    if (lo > hi) return null;

    for (let r = 0; r <= maxRadius; r++) {
        const a = o - r;
        const b = o + r;

        if (a >= lo && a <= hi && pcSet.has(pcOfMidi(a))) return a;
        if (b !== a && b >= lo && b <= hi && pcSet.has(pcOfMidi(b))) return b;
    }

    return null;
}

/**
 * Viterbi over melody items: choose target midi for each item that preserves contour
 * and prefers chord tones on strong beats / longer notes / last note.
 */
export function viterbiMelodyTargets(
    melody: MelodyItem[],
    measure: Measure,
    chordPcSet: Set<number>,
    scalePcSet: Set<number>
): number[] {
    const N = melody.length;
    if (N === 0) return [];

    const div = measure.divisions;
    const beatsPerMeas = quarterBeatsPerMeasure(measure);

    const maxRadius = 12;

    const candidates = melody.map((m) => enumerateCandidatesMidi(m.origMidi, scalePcSet, maxRadius));

    // DP tables
    const dp: number[][] = [];
    const back: number[][] = [];

    const origMidis = melody.map((m) => m.origMidi);

    for (let i = 0; i < N; i++) {
        dp[i] = new Array(candidates[i].length).fill(Number.POSITIVE_INFINITY);
        back[i] = new Array(candidates[i].length).fill(-1);
    }

    const emissionCost = (i: number, candMidi: number): number => {
        const m = melody[i];

        const startBeat = m.startDiv / div;
        const beatInMeasure = ((startBeat % beatsPerMeas) + beatsPerMeas) % beatsPerMeas;
        const bs = beatStrength(beatInMeasure, beatsPerMeas);

        const durBeats = m.durDiv / div;

        const isLast = i === N - 1;

        // harmonic importance increases on strong beats, long notes, and measure end
        const strongness = clamp01((bs - 0.45) / 0.65);
        const lengthness = clamp01((durBeats - 0.25) / 1.25);
        const endness = isLast ? 1 : 0;

        const importance = clamp01(0.55 * strongness + 0.35 * lengthness + 0.35 * endness);

        const dist = Math.abs(candMidi - m.origMidi);

        const pc = pcOfMidi(candMidi);
        const chordTone = isChordTonePc(pc, chordPcSet);

        // Distance: small penalty
        let cost = dist * 0.22;

        // Prefer chord tones when important; allow scale passing tones when weak
        if (!chordTone) {
            cost += importance * 3.5; // push strong/long/end notes onto chord tones
        } else {
            cost -= importance * 0.35; // mild reward
        }

        // discourage very large register jumps away from original over time
        if (dist >= 10) cost += (dist - 9) * 0.35;

        return cost;
    };

    const transitionCost = (i: number, prevCand: number, cand: number): number => {
        if (i === 0) return 0;

        const origInterval = origMidis[i] - origMidis[i - 1];
        const newInterval = cand - prevCand;

        const dirO = Math.sign(origInterval);
        const dirN = Math.sign(newInterval);

        let cost = 0;

        // preserve interval shape
        cost += Math.abs(newInterval - origInterval) * 0.08;

        // preserve direction (unless one of them is unison)
        if (dirO !== 0 && dirN !== 0 && dirO !== dirN) cost += 0.55;

        // discourage huge leaps
        const leap = Math.abs(newInterval);
        if (leap > 7) cost += (leap - 7) * 0.16;

        return cost;
    };

    // init
    for (let k = 0; k < candidates[0].length; k++) {
        dp[0][k] = emissionCost(0, candidates[0][k]);
    }

    // fill
    for (let i = 1; i < N; i++) {
        for (let k = 0; k < candidates[i].length; k++) {
            const cand = candidates[i][k];
            const em = emissionCost(i, cand);

            let best = Number.POSITIVE_INFINITY;
            let bestPrev = -1;

            for (let pk = 0; pk < candidates[i - 1].length; pk++) {
                const prev = candidates[i - 1][pk];
                const v = dp[i - 1][pk] + transitionCost(i, prev, cand) + em;
                if (v < best) {
                    best = v;
                    bestPrev = pk;
                }
            }

            dp[i][k] = best;
            back[i][k] = bestPrev;
        }
    }

    // backtrack
    let bestEndK = 0;
    let bestEnd = dp[N - 1][0];
    for (let k = 1; k < dp[N - 1].length; k++) {
        if (dp[N - 1][k] < bestEnd) {
            bestEnd = dp[N - 1][k];
            bestEndK = k;
        }
    }

    const targets = new Array<number>(N);
    let k = bestEndK;
    for (let i = N - 1; i >= 0; i--) {
        targets[i] = candidates[i][k];
        k = back[i][k] >= 0 ? back[i][k] : 0;
    }

    return targets;
}

export function snapSingleNoteMidi(
    origMidi: number,
    measure: Measure,
    startDiv: number,
    durDiv: number,
    chordPcSet: Set<number>,
    scalePcSet: Set<number>
): number {
    const div = measure.divisions;
    const beatsPerMeas = quarterBeatsPerMeasure(measure);

    const startBeat = startDiv / div;
    const beatInMeasure = ((startBeat % beatsPerMeas) + beatsPerMeas) % beatsPerMeas;
    const bs = beatStrength(beatInMeasure, beatsPerMeas);

    const durBeats = durDiv / div;

    const preferChord = bs >= 0.75 || durBeats >= 0.75;

    // Prefer chord tones on strong beats/long notes; otherwise scale tones.
    if (preferChord) {
        const chordMidi = nearestMidiInPcSet(origMidi, chordPcSet, 0, 127, 12);
        if (chordMidi != null) return chordMidi;

        const scaleMidi = nearestMidiInPcSet(origMidi, scalePcSet, 0, 127, 12);
        return scaleMidi ?? clamp(Math.round(origMidi), 0, 127);
    }

    const scaleMidi = nearestMidiInPcSet(origMidi, scalePcSet, 0, 127, 12);
    if (scaleMidi == null) return clamp(Math.round(origMidi), 0, 127);

    // If a chord tone is essentially as close, pick chord tone.
    const chordMidi = nearestMidiInPcSet(origMidi, chordPcSet, 0, 127, 12);
    if (chordMidi != null && Math.abs(chordMidi - origMidi) <= Math.abs(scaleMidi - origMidi) + 0.25) {
        return chordMidi;
    }

    return scaleMidi;
}

/**
 * Retune a chord voicing by snapping each note to a chord tone,
 * preserving ordering (no crossing). Optionally lock the top note.
 */
export function retuneChordVoicing(
    notes: PitchedNote[],
    chordPcSet: Set<number>,
    scalePcSet: Set<number>,
    key: Key,
    lockedTop?: { note: PitchedNote; midi: number }
): void {
    if (notes.length === 0) return;

    const sorted = [...notes].sort((a, b) => a.pitch.getMidiPitch() - b.pitch.getMidiPitch());

    // If locking top, ensure we treat that note as the highest (even if rounding shifts).
    let lockIdx = -1;
    if (lockedTop) {
        for (let i = 0; i < sorted.length; i++) {
            if (sorted[i] === lockedTop.note) {
                lockIdx = i;
                break;
            }
        }
    }

    // Assign targets
    const targets = new Map<PitchedNote, number>();

    // Lock top (usually melody voice chord)
    if (lockedTop && lockIdx >= 0) {
        targets.set(lockedTop.note, clamp(Math.round(lockedTop.midi), 0, 127));
    }

    let prevAssigned = -1;

    for (let i = 0; i < sorted.length; i++) {
        const n = sorted[i];

        if (lockedTop && n === lockedTop.note) {
            const m = targets.get(n)!;
            prevAssigned = m;
            continue;
        }

        const orig = n.pitch.getMidiPitch();
        const hi = (lockedTop && lockIdx >= 0 && i < lockIdx)
            ? (targets.get(lockedTop.note)! - 1)
            : 127;

        const lo = prevAssigned + 1;

        // Prefer chord tones; fall back to scale tones if needed
        let m = nearestMidiInPcSet(orig, chordPcSet, lo, hi, 24);
        if (m == null) m = nearestMidiInPcSet(orig, scalePcSet, lo, hi, 24);
        if (m == null) m = clamp(Math.round(orig), lo, hi);

        targets.set(n, m);
        prevAssigned = m;
    }

    // Apply
    for (const n of sorted) {
        const target = targets.get(n);
        if (target == null) continue;
        applyShiftToMidi(n, target, key);
    }
}